import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateWeddingInput,
  MemberRole,
  UpdateProfileInput,
  UpdateWeddingInput,
  UserProfile,
  Wedding,
  WeddingSummary,
  Workspace,
} from '@guabi/shared';
import { apiGet, apiPatch, apiPost } from '@/lib/api';

export interface MeResponse {
  profile: UserProfile;
  workspaces: Workspace[];
  weddings: Wedding[];
  /** The caller's role per workspace, so the UI can hide what the server would refuse. */
  memberships: { wsId: string; role: MemberRole }[];
}

export const meQueryKey = ['me'] as const;

export function useMe() {
  return useQuery({ queryKey: meQueryKey, queryFn: () => apiGet<MeResponse>('/me') });
}

export function useWeddingSummary(wedId: string | undefined) {
  return useQuery({
    queryKey: ['summary', wedId],
    queryFn: () => apiGet<WeddingSummary>(`/weddings/${wedId}/summary`),
    enabled: Boolean(wedId),
  });
}

/** Creates the workspace on demand when the couple has none, then the wedding. */
export function useCreateWedding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateWeddingInput & { wsId?: string }) => {
      const { wsId, ...wedding } = input;
      let workspaceId = wsId;
      if (!workspaceId) {
        const ws = await apiPost<Workspace>('/workspaces', {
          name: wedding.title,
          kind: 'couple',
        });
        workspaceId = ws.wsId;
      }
      return apiPost<Wedding>(`/workspaces/${workspaceId}/weddings`, wedding);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: meQueryKey }),
  });
}

/**
 * Edits an existing wedding. `updatedAt` goes up with the change so the server
 * can reject a write made against a stale copy — which matters as soon as more
 * than one person can edit the same wedding.
 */
export function useUpdateWedding(wedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWeddingInput & { updatedAt: string }) =>
      apiPatch<Wedding>(`/weddings/${wedId}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meQueryKey });
      void qc.invalidateQueries({ queryKey: ['summary', wedId] });
    },
  });
}

/**
 * The caller's own preferences: the language they read and the weekly mail.
 *
 * Optimistic, because both are toggles — a switch that waits for a round trip
 * before it looks switched reads as broken, and there is nothing to lose to a
 * concurrent writer.
 */
export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => apiPatch<UserProfile>('/me', input),
    onMutate: (input) => {
      const previous = qc.getQueryData<MeResponse>(meQueryKey);
      if (previous) {
        qc.setQueryData<MeResponse>(meQueryKey, {
          ...previous,
          profile: { ...previous.profile, ...input },
        });
      }
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) qc.setQueryData(meQueryKey, context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: meQueryKey }),
  });
}
